import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { 
  Box, 
  Container, 
  Stack, 
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';

import { ReportsDashboard } from 'components/Reports';
import { useApi } from '../../hooks/useApi';

interface Agent {
  id: number;
  name: string;
}

export const Reports = () => {
  const { user } = useUser();
  const { apiFetch } = useApi();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    (async () => {
      try {
        const res = await apiFetch('agents');
        const data = await res.json();
        setAgents(data || []);
        
        // Auto-select first agent with Teams integration
        if (data && data.length > 0) {
          // For now, just select the first agent
          // In production, you might want to filter by those with quiz_sessions
          setSelectedAgentId(data[0].id);
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  return (
    <Container maxWidth="xl">
      <Stack spacing={4}>
        {/* Page Header */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AssessmentIcon sx={{ color: '#0099CC', fontSize: 32 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
                Quiz Reports
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
              Monitor quiz participation, performance, and team progress
            </Typography>
          </Box>

          {/* Agent Selector */}
          {agents.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Agent</InputLabel>
              <Select
                value={selectedAgentId || ''}
                label="Select Agent"
                onChange={(e) => setSelectedAgentId(e.target.value as number)}
                sx={{ borderRadius: '8px' }}
              >
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        {/* Dashboard */}
        {loading ? (
          <Stack spacing={3}>
            <Stack direction="row" spacing={2}>
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} variant="rounded" width="20%" height={140} />
              ))}
            </Stack>
            <Skeleton variant="rounded" width="100%" height={400} />
          </Stack>
        ) : selectedAgentId ? (
          <ReportsDashboard agentId={selectedAgentId} />
        ) : (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 8,
              backgroundColor: '#F9FAFB',
              borderRadius: '16px',
              border: '1px dashed #E5E7EB',
            }}
          >
            <AssessmentIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6B7280' }}>
              No agents found
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 1 }}>
              Create an agent with Teams integration to start tracking quiz performance
            </Typography>
          </Box>
        )}
      </Stack>
    </Container>
  );
};
